// src/components/TourMap.jsx
// Map overlay for wine tour route visualization
// Uses Leaflet + OpenStreetMap tiles + OSRM for routing

import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, MapPin, Clock, Star, Navigation } from "lucide-react";

// Depot city coordinates
const DEPOT_COORDS = {
  "Vienna":   [48.1200, 16.3200],
  "Maribor":  [46.5200, 15.6200],
  "Gorizia":  [45.9500, 13.6500],
  "Venice":   [45.4700, 12.2300],
  "Venezia":  [45.4408, 12.3155],
  "Bonn":     [50.7374,  7.0982],
  "Ahrweiler":[50.5228,  7.0937],
  "Altenahr": [50.5097,  6.9931],
  "Krakow":   [50.0647, 19.9450],
  "Tarnow":   [50.0121, 20.9858],
  "Rzeszow":  [50.0412, 21.9991],
};
function getCoords(name, wineryMap) {
  if (DEPOT_COORDS[name]) return DEPOT_COORDS[name];
  if (wineryMap && wineryMap[name]) return wineryMap[name];
  return null;
}

// Fetch route from OSRM (free, no API key needed)
async function fetchRoute(waypoints) {
  const coords = waypoints
    .map(([lat, lng]) => `${lng},${lat}`)
    .join(";");
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data.code === "Ok" && data.routes[0]) {
      return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    }
  } catch (e) {
    console.warn("OSRM routing failed:", e);
  }
  return null;
}

const DEPOT_NODES = new Set(["Vienna", "Maribor", "Gorizia", "Venice", "Warsaw", "Krakow", "Ahr"]);

const WINERY_COLORS = {
  depot: "#6A7E58",
  winery: "#6B2737",
};

// Tour mode (round trip, see model.mod's RoundTrip param): outbound
// segments 1..half keep the existing brand red; return segments
// half+1..m get a distinct blue so the two directions read apart on the
// map at a glance. Route mode (the only mode before this feature) has no
// return leg at all, so ROUTE_COLOR is what every existing tour still
// draws with — zero visual change there.
const ROUTE_COLOR = "#6B2737";
const RETURN_COLOR = "#3B6B8C";

// A round trip is detected from tour.legs' own segment numbers (set by
// solver-a/handler.py's get_legs — see model.mod's RoundTrip comment for
// why outbound is always segments 1..half and return is always
// half+1..maxSegment for m=2*half), not from any separate "is this a
// Tour" flag — this keeps the map self-contained even if that flag isn't
// threaded all the way through to whatever shape `tour` ends up in here.
function roundTripSplit(legs) {
  const maxSegment = legs.length ? Math.max(...legs.map(l => l.segment || 0)) : 0;
  if (maxSegment < 4) return null; // Route mode is always m=3 — no return leg to split off
  const half = Math.ceil(maxSegment / 2);
  const firstReturnLeg = legs.find(l => l.segment === half + 1);
  if (!firstReturnLeg) return null;
  return { half, turnaroundName: firstReturnLeg.from };
}

export default function TourMap({ tour, onClose, onBookWinery }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMarker, setSelectedMarker] = useState(null);

  const route = tour?.optimal_route || [];
  // Computed once per tour, reused by both the header legend and the
  // bottom route strip below (initMap recomputes its own copy internally
  // since it runs in a separate async callback — see roundTripSplit).
  const split = React.useMemo(() => roundTripSplit(tour?.legs || []), [tour]);
  const splitIdxInRoute = split ? route.indexOf(split.turnaroundName) : -1;

  const initMap = useCallback(async () => {
    if (!window.L || !mapRef.current || mapInstanceRef.current) return;

    // Build winery coordinate map from tour data
    const wineryMap = {};
    if (tour?.days) {
      tour.days.forEach(day => {
        (day.wineries || []).forEach(w => {
          if (w.name && (w.latitude || w.lat) && (w.longitude || w.lng)) {
            wineryMap[w.name] = [parseFloat(w.latitude || w.lat), parseFloat(w.longitude || w.lng)];
          }
        });
      });
    }
    const L = window.L;

    // Get coordinates for all stops
    const stops = route.map(name => ({
      name,
      coords: getCoords(name, wineryMap),
      isDepot: DEPOT_NODES.has(name),
    })).filter(s => s.coords);

    if (stops.length < 2) {
      setError("Not enough location data for this route.");
      setLoading(false);
      return;
    }

    // Center map on route midpoint
    const lats = stops.map(s => s.coords[0]);
    const lngs = stops.map(s => s.coords[1]);
    const center = [(Math.min(...lats) + Math.max(...lats)) / 2,
                    (Math.min(...lngs) + Math.max(...lngs)) / 2];

    const map = L.map(mapRef.current, {
      center,
      zoom: 7,
      zoomControl: true,
    });
    mapInstanceRef.current = map;

    // OSM tiles
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);

    // Tour mode: draw outbound and return as two separately-colored
    // polylines (see roundTripSplit) instead of one continuous line — the
    // split point (the turnaround city, e.g. Venice) is included in BOTH
    // halves so the two lines visually connect with no gap. Recomputed
    // locally (not reusing the component-scope `split` above) so this
    // async callback never reads a stale closure value.
    const splitInfo = roundTripSplit(tour?.legs || []);
    const splitIdx = splitInfo ? stops.findIndex(s => s.name === splitInfo.turnaroundName) : -1;
    const legSegments = (splitInfo && splitIdx !== -1)
      ? [
          { pts: stops.slice(0, splitIdx + 1), color: ROUTE_COLOR },
          { pts: stops.slice(splitIdx), color: RETURN_COLOR },
        ]
      : [{ pts: stops, color: ROUTE_COLOR }];

    for (const { pts, color } of legSegments) {
      if (pts.length < 2) continue;
      const waypoints = pts.map(s => s.coords);
      const routeLine = await fetchRoute(waypoints);
      if (routeLine) {
        // Draw actual road route
        L.polyline(routeLine, {
          color, weight: 4, opacity: 0.8, dashArray: null,
        }).addTo(map);
      } else {
        // Fallback: straight lines between stops
        L.polyline(waypoints, {
          color, weight: 3, opacity: 0.6, dashArray: "8 4",
        }).addTo(map);
      }
    }

    // Add markers for each stop
    stops.forEach((stop, idx) => {
      const isDepot = stop.isDepot;
      const size = isDepot ? 14 : 20;
      const color = isDepot ? WINERY_COLORS.depot : WINERY_COLORS.winery;

      const icon = L.divIcon({
        html: `
          <div style="
            width:${size}px; height:${size}px; border-radius:50%;
            background:${color}; border:2px solid rgba(255,255,255,0.8);
            display:flex; align-items:center; justify-content:center;
            font-size:9px; color:white; font-weight:700;
            box-shadow:0 2px 6px rgba(0,0,0,0.5);
            cursor:pointer;
          ">${isDepot ? "" : (idx + 1)}</div>
        `,
        className: "",
        iconSize: [size, size],
        iconAnchor: [size/2, size/2],
      });

      // Find winery details from tour
      const wineryDetail = tour?.days?.flatMap(d => d.wineries || [])
        .find(w => w.name === stop.name);

      const popupContent = `
        <div style="font-family:Inter,sans-serif; min-width:180px; padding:4px;">
          <div style="font-weight:600; font-size:13px; color:#1a1a1a; margin-bottom:4px;">
            ${stop.name}
          </div>
          ${isDepot ? `<div style="font-size:11px; color:#666;">Gateway city</div>` : `
            ${wineryDetail?.description ? `<div style="font-size:11px; color:#555; margin-bottom:6px;">${wineryDetail.description}</div>` : ""}
            ${wineryDetail?.avg_rating ? `<div style="font-size:11px; color:#886070;">★ ${wineryDetail.avg_rating}</div>` : ""}
            ${wineryDetail?.highlights?.length ? `
              <div style="margin-top:4px; display:flex; gap:4px; flex-wrap:wrap;">
                ${wineryDetail.highlights.slice(0,2).map(h => `
                  <span style="font-size:10px; padding:2px 6px; background:#f5f0f0; border-radius:10px; color:#6B2737;">${h}</span>
                `).join("")}
              </div>
            ` : ""}
            <button onclick="window._bookTasting('${stop.name}')"
              style="margin-top:8px; width:100%; padding:5px; background:#6B2737; color:white;
                     border:none; border-radius:5px; font-size:11px; cursor:pointer;">
              Book tasting
            </button>
          `}
        </div>
      `;

      const marker = L.marker(stop.coords, { icon })
        .bindPopup(popupContent, { maxWidth: 220 })
        .addTo(map);
    });

    // Fit map to show all stops
    const bounds = L.latLngBounds(stops.map(s => s.coords));
    map.fitBounds(bounds, { padding: [40, 40] });

    setLoading(false);
  }, [route, tour]);

  useEffect(() => {
    // Global booking handler — the popup content above is raw HTML (Leaflet
    // popups aren't React-rendered), so its "Book tasting" button can only
    // reach app state through a window global, not a normal prop/closure.
    // Resolves the clicked stop's legIndex from tour.legs (matching by
    // name, same as the wineryDetail lookup above) and hands off to the
    // exact same openBookingForLeg flow the itinerary's own "Book tasting"
    // buttons use, so this stays trip-aware/live-feasibility instead of
    // being a separate, disconnected booking path. Previously this was a
    // stub that only console.logged the winery name and did nothing else.
    window._bookTasting = (name) => {
      const legIndex = (tour?.legs || []).findIndex(
        l => l.is_winery_stop && l.to === name
      );
      if (legIndex === -1) {
        console.warn("Book tasting: couldn't resolve a leg for", name);
        return;
      }
      onBookWinery?.(legIndex);
    };

    // Load Leaflet dynamically
    if (window.L) {
      initMap();
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => initMap();
    script.onerror = () => setError("Failed to load map library.");
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      delete window._bookTasting;
    };
  }, [initMap, tour, onBookWinery]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(242,245,236,0.95)", backdropFilter: "blur(4px)",
      display: "flex", flexDirection: "column",
      animation: "fadeIn 0.2s ease-out",
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 20px", background: "rgba(242,245,236,0.98)",
        borderBottom: "1px solid #252525", flexShrink: 0,
      }}>
        <div>
          <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, color: "#1E2C14" }}>
            Route Map — {tour?.road_name || "Wine Tour"}
          </div>
          <div style={{ fontSize: 11, color: "#6A7E58", marginTop: 2 }}>
            {route.filter(n => !DEPOT_NODES.has(n)).length} wineries · {tour?.min_time_minutes ? `${Math.floor(tour.min_time_minutes/60)}h ${tour.min_time_minutes%60}min total` : ""}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Route legend */}
          <div style={{ display: "flex", gap: 12, fontSize: 10, color: "#6A7E58" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#6B2737" }} />
              Winery
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#6A7E58" }} />
              City
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 20, height: 2, background: ROUTE_COLOR }} />
              {split ? "Outbound" : "Route"}
            </div>
            {split && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 20, height: 2, background: RETURN_COLOR }} />
                Return
              </div>
            )}
          </div>

          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "#E8EDE0", border: "1px solid #2A2A2A",
            color: "#2C3820", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Map container */}
      <div style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 12,
            background: "#F2F5EC",
          }}>
            <div style={{ fontSize: 28 }}>🗺️</div>
            <div style={{ fontSize: 14, color: "#2C3820" }}>Loading route map…</div>
            <div style={{ fontSize: 11, color: "#6A7E58" }}>Fetching road data from OpenStreetMap</div>
          </div>
        )}
        {error && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "#F2F5EC", color: "#886070", fontSize: 14,
          }}>
            {error}
          </div>
        )}
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      </div>

      {/* Route strip at bottom */}
      <div style={{
        background: "rgba(242,245,236,0.98)", borderTop: "1px solid #1E1E1E",
        padding: "10px 20px", flexShrink: 0,
        display: "flex", alignItems: "center", gap: 6, overflowX: "auto",
      }}>
        {route.map((name, i) => (
          <React.Fragment key={i}>
            <div style={{
              display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
            }}>
              <div style={{
                width: 7, height: 7, borderRadius: "50%",
                background: DEPOT_NODES.has(name) ? "#6A7E58" : "#6B2737",
                flexShrink: 0,
              }} />
              <span style={{
                fontSize: 11,
                color: DEPOT_NODES.has(name) ? "#6A7E58" : "#2C3820",
                fontWeight: DEPOT_NODES.has(name) ? 400 : 500,
                whiteSpace: "nowrap",
              }}>{name}</span>
            </div>
            {i < route.length - 1 && (
              <span style={{
                color: (split && i >= splitIdxInRoute && splitIdxInRoute !== -1) ? RETURN_COLOR : "#2E3D48",
                fontSize: 10, flexShrink: 0,
              }}>→</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
